import { useState } from "react";
import { Combobox } from "@headlessui/react";
import useDebounce from "../../hooks/use-debounce";
import useSearchResults from "../../hooks/use-search-results";
import usePlace from "../../hooks/use-place";
import useGeolocate from "../../hooks/use-geolocate";

export default function MapSearchBar({
  defaultSearchTerm,
  onCoordinatesChange,
  className,
}) {
  const [searchTerm, setSearchTerm] = useState(defaultSearchTerm);
  const [selected, setSelected] = useState(null);

  // Debounce user input to avoid hammering the search API
  const debouncedTerm = useDebounce(searchTerm, 300);

  // Fetch place autocomplete results
  const { data: placeResults } = useSearchResults({ search: debouncedTerm });

  // Fetch place details (lat/lng) from a place_id
  const { mutate: getPlace } = usePlace();

  // Custom geolocation hook
  const { mutate: geolocate } = useGeolocate();

  // User selects a place from the dropdown
  const handlePlaceSelect = (value) => {
    setSelected(value);
    getPlace(
      { placeId: value.place_id },
      {
        onSuccess: (data) => {
          onCoordinatesChange({
            lat: data?.geometry?.location?.lat,
            lng: data?.geometry?.location?.lng,
          });
        },
      }
    );
  };

  // User clicks on the geolocate icon
  const handleGeolocateClick = () => {
    geolocate(undefined, {
      onSuccess: (data) => {
        onCoordinatesChange({
          lat: data.coords.latitude,
          lng: data.coords.longitude,
        });
      },
      onError: (error) => {
        console.error('Geolocation error:', error);
      },
    });
  };

  return (
    <div className={className}>
      <Combobox
        value={selected?.description ?? ""}
        onChange={handlePlaceSelect}
      >
        <Combobox.Input
          placeholder="Enter your postcode or city..."
          defaultValue={defaultSearchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-map__search-input"
        />

        {/* Geolocation icon that triggers the location request */}
        <img
          src="/app/themes/adaptable/assets/dist/images/location.png"
          alt="Location Icon"
          className="search-map__image"
          onClick={handleGeolocateClick}
        />

        <Combobox.Options className="search-map__search-results">
          {placeResults?.length > 0 &&
            placeResults.map((result) => (
              <Combobox.Option
                key={result.place_id}
                value={result}
                className="search-map__search-results-item"
              >
                <p>{result?.description}</p>
              </Combobox.Option>
            ))}
        </Combobox.Options>
      </Combobox>
    </div>
  );
}
